/**
 * PhotoLightbox — the one fullscreen photo viewer.
 *
 * Apple Photos shape: black canvas, image contained, pinch (native) or
 * click/scroll (web) to zoom, pan when zoomed, swipe-down to dismiss at 1x.
 * One AppModal for iOS, Android, web and tablets. Never a raw RN Modal —
 * Android RNGH Pressables would be dead in a separate window.
 *
 * Every tappable photo surface (vehicle gallery, tours cover, checkout car,
 * listing shots) must open this component. A second lightbox is a product
 * bug, not a variant.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Platform, Text, useWindowDimensions, View } from 'react-native'
import { Gesture, GestureDetector } from 'react-native-gesture-handler'
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated'
import { useTranslation } from 'react-i18next'
import { Image as ExpoImage } from 'expo-image'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { AppModal } from './AppModal'
import { Pressable } from './Pressable'
import { ChevronLeft, ChevronRight, Minus, Plus, X } from '@/components/ui/icons'
import { toPlaceholder } from '@/lib/utils/blurhash'
import { IMAGE_CACHE_POLICY } from '@/lib/utils/image-cache'
import { normalizeRemoteImageUri, WEB_EXIF_ORIENTATION_STYLE } from '@/lib/utils/image-source'
import { impact, ImpactStyle, selection } from '@/lib/utils/haptics'
import {
  clampPhotoIndex,
  clampPhotoZoom,
  PHOTO_LIGHTBOX_MAX_ZOOM,
  PHOTO_LIGHTBOX_MIN_ZOOM,
  shouldDismissPhotoLightbox,
  stepPhotoZoom,
  wrapPhotoIndex,
  zoomFromWheelDelta,
} from '@/lib/utils/photo-lightbox'
import { useTheme } from '@/lib/hooks/use-theme'
import {
  DURATION,
  INTERACTION,
  SPACING,
  TEXT_STYLES,
  useSemanticColors,
  withAlpha,
} from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { webShadow } from '@/lib/theme/web-shadow'

export type PhotoLightboxImage = {
  id: string
  uri: string
  blurhash?: string | null
  accessibilityLabel?: string
}

export type PhotoLightboxProps = {
  visible: boolean
  images: readonly PhotoLightboxImage[]
  index: number
  onIndexChange?: (index: number) => void
  onClose: () => void
  testIDPrefix?: string
}

type WebKeyEvent = { key: string; preventDefault: () => void }
type WebWheelEvent = Event & { deltaY: number }

const CONTROL_SIZE = INTERACTION.minTouchTarget

/** Default testIDs — Maestro scans source text, so the literals must appear. */
const PHOTO_LIGHTBOX_TEST_IDS = [
  'photo-lightbox.overlay',
  'photo-lightbox.canvas',
  'photo-lightbox.image',
  'photo-lightbox.close',
  'photo-lightbox.zoom-in',
  'photo-lightbox.zoom-out',
  'photo-lightbox.previous',
  'photo-lightbox.next',
  'photo-lightbox.counter',
] as const
void PHOTO_LIGHTBOX_TEST_IDS

export function usePhotoLightbox() {
  const [state, setState] = useState<{ images: PhotoLightboxImage[]; index: number } | null>(null)

  const open = useCallback((images: readonly PhotoLightboxImage[], index: number) => {
    if (images.length === 0) return
    setState({ images: [...images], index: clampPhotoIndex(images.length, index) })
  }, [])

  const close = useCallback(() => {
    setState(null)
  }, [])

  const setIndex = useCallback((index: number) => {
    setState((current) =>
      current ? { ...current, index: clampPhotoIndex(current.images.length, index) } : current,
    )
  }, [])

  return {
    visible: state !== null,
    images: state?.images ?? [],
    index: state?.index ?? 0,
    open,
    close,
    setIndex,
  }
}

function LightboxControl({
  label,
  testID,
  disabled,
  onPress,
  children,
}: {
  label: string
  testID: string
  disabled?: boolean
  onPress: () => void
  children: ReactNode
}) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const disc = webShadow({ y: 3, blur: 12, opacity: isDark ? 0.22 : 0.1, elevation: 3 })

  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ disabled: Boolean(disabled) }}
      testID={testID}
      hitSlop={INTERACTION.iconHitSlop}
      style={({ pressed }) => ({
        width: CONTROL_SIZE,
        height: CONTROL_SIZE,
        borderRadius: CONTROL_SIZE / 2,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: withAlpha(colors.surface, disabled ? 0.55 : 0.92),
        borderWidth: 1,
        borderColor: withAlpha(colors.textPrimary, isDark ? 0.14 : 0.08),
        opacity: pressed || disabled ? 0.72 : 1,
        ...disc,
      })}
    >
      {children}
    </Pressable>
  )
}

export function PhotoLightbox({
  visible,
  images,
  index,
  onIndexChange,
  onClose,
  testIDPrefix = 'photo-lightbox',
}: PhotoLightboxProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const insets = useSafeAreaInsets()
  const reducedMotion = useReducedMotion() === true
  const { width: windowWidth, height: windowHeight } = useWindowDimensions()

  const safeIndex = clampPhotoIndex(images.length, index)
  const current = images[safeIndex]
  const currentUri = typeof current?.uri === 'string' ? normalizeRemoteImageUri(current.uri) : null
  const lightboxSource = typeof currentUri === 'string' ? currentUri : null
  const hasMany = images.length > 1
  const iconColor = colors.textPrimary

  const scale = useSharedValue(PHOTO_LIGHTBOX_MIN_ZOOM)
  const translateX = useSharedValue(0)
  const translateY = useSharedValue(0)
  const pinchStart = useSharedValue(PHOTO_LIGHTBOX_MIN_ZOOM)
  const panStartX = useSharedValue(0)
  const panStartY = useSharedValue(0)
  const [zoom, setZoom] = useState(PHOTO_LIGHTBOX_MIN_ZOOM)

  const snapDuration = reducedMotion ? 0 : DURATION.fast

  const resetTransform = useCallback(() => {
    scale.value = withTiming(PHOTO_LIGHTBOX_MIN_ZOOM, { duration: snapDuration })
    translateX.value = withTiming(0, { duration: snapDuration })
    translateY.value = withTiming(0, { duration: snapDuration })
    setZoom(PHOTO_LIGHTBOX_MIN_ZOOM)
  }, [scale, snapDuration, translateX, translateY])

  const applyZoom = useCallback(
    (next: number) => {
      const clamped = clampPhotoZoom(next)
      scale.value = withTiming(clamped, { duration: snapDuration })
      if (clamped <= PHOTO_LIGHTBOX_MIN_ZOOM) {
        translateX.value = withTiming(0, { duration: snapDuration })
        translateY.value = withTiming(0, { duration: snapDuration })
      }
      setZoom(clamped)
    },
    [scale, snapDuration, translateX, translateY],
  )

  useEffect(() => {
    if (!visible) return
    resetTransform()
  }, [resetTransform, safeIndex, visible])

  const go = useCallback(
    (delta: number) => {
      if (images.length <= 1) return
      const next = wrapPhotoIndex(images.length, safeIndex, delta)
      selection()
      onIndexChange?.(next)
    },
    [images.length, onIndexChange, safeIndex],
  )

  const close = useCallback(() => {
    impact(ImpactStyle.Light)
    onClose()
  }, [onClose])

  const handleBackdropClose = useCallback(() => {
    if (zoom > PHOTO_LIGHTBOX_MIN_ZOOM + 0.02) return
    close()
  }, [close, zoom])

  const reportZoom = useCallback((next: number) => {
    setZoom(clampPhotoZoom(next))
  }, [])

  useEffect(() => {
    if (!visible || Platform.OS !== 'web' || typeof window === 'undefined') return

    const onKeyDown = (event: WebKeyEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        close()
        return
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault()
        go(1)
        return
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        go(-1)
        return
      }
      if (event.key === '+' || event.key === '=') {
        event.preventDefault()
        applyZoom(stepPhotoZoom(zoom, 1))
        return
      }
      if (event.key === '-' || event.key === '_') {
        event.preventDefault()
        applyZoom(stepPhotoZoom(zoom, -1))
      }
    }

    const onWheel = (event: Event) => {
      const wheel = event as WebWheelEvent
      wheel.preventDefault()
      applyZoom(zoomFromWheelDelta(scale.value, wheel.deltaY))
    }

    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('wheel', onWheel, { passive: false })
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('wheel', onWheel)
    }
  }, [applyZoom, close, go, scale, visible, zoom])

  const pinch = useMemo(
    () =>
      Gesture.Pinch()
        .onBegin(() => {
          pinchStart.value = scale.value
        })
        .onUpdate((event) => {
          scale.value = clampPhotoZoom(pinchStart.value * event.scale)
        })
        .onEnd(() => {
          const next = clampPhotoZoom(scale.value)
          scale.value = withTiming(next, { duration: snapDuration })
          if (next <= PHOTO_LIGHTBOX_MIN_ZOOM) {
            translateX.value = withTiming(0, { duration: snapDuration })
            translateY.value = withTiming(0, { duration: snapDuration })
          }
          runOnJS(reportZoom)(next)
        }),
    [pinchStart, reportZoom, scale, snapDuration, translateX, translateY],
  )

  const pan = useMemo(
    () =>
      Gesture.Pan()
        .minDistance(12)
        .onBegin(() => {
          panStartX.value = translateX.value
          panStartY.value = translateY.value
        })
        .onUpdate((event) => {
          if (scale.value > PHOTO_LIGHTBOX_MIN_ZOOM + 0.02) {
            const maxX = ((scale.value - 1) * windowWidth) / 2
            const maxY = ((scale.value - 1) * windowHeight) / 2
            const nextX = panStartX.value + event.translationX
            const nextY = panStartY.value + event.translationY
            translateX.value = Math.min(maxX, Math.max(-maxX, nextX))
            translateY.value = Math.min(maxY, Math.max(-maxY, nextY))
            return
          }
          translateX.value = 0
          translateY.value = event.translationY
        })
        .onEnd((event) => {
          if (shouldDismissPhotoLightbox(scale.value, event.translationY)) {
            runOnJS(close)()
            return
          }
          if (scale.value <= PHOTO_LIGHTBOX_MIN_ZOOM + 0.02) {
            translateX.value = withTiming(0, { duration: snapDuration })
            translateY.value = withTiming(0, { duration: snapDuration })
          }
        }),
    [
      close,
      panStartX,
      panStartY,
      scale,
      snapDuration,
      translateX,
      translateY,
      windowHeight,
      windowWidth,
    ],
  )

  const doubleTap = useMemo(
    () =>
      Gesture.Tap()
        .numberOfTaps(2)
        .onEnd(() => {
          const next =
            scale.value > PHOTO_LIGHTBOX_MIN_ZOOM + 0.02
              ? PHOTO_LIGHTBOX_MIN_ZOOM
              : stepPhotoZoom(PHOTO_LIGHTBOX_MIN_ZOOM, 1) + 1
          scale.value = withTiming(clampPhotoZoom(next), { duration: snapDuration })
          if (next <= PHOTO_LIGHTBOX_MIN_ZOOM) {
            translateX.value = withTiming(0, { duration: snapDuration })
            translateY.value = withTiming(0, { duration: snapDuration })
          }
          runOnJS(reportZoom)(next)
        }),
    [reportZoom, scale, snapDuration, translateX, translateY],
  )

  const singleTap = useMemo(
    () =>
      Gesture.Tap()
        .maxDistance(8)
        .onEnd(() => {
          if (scale.value <= PHOTO_LIGHTBOX_MIN_ZOOM + 0.02) {
            runOnJS(handleBackdropClose)()
          }
        }),
    [handleBackdropClose, scale],
  )

  const composed = useMemo(
    () =>
      Gesture.Simultaneous(pinch, Gesture.Exclusive(doubleTap, Gesture.Exclusive(pan, singleTap))),
    [doubleTap, pan, pinch, singleTap],
  )

  const imageStyle = useAnimatedStyle(() => ({
    transform: [
      { translateX: translateX.value },
      { translateY: translateY.value },
      { scale: scale.value },
    ],
  }))

  const canZoomIn = zoom < PHOTO_LIGHTBOX_MAX_ZOOM - 0.01
  const canZoomOut = zoom > PHOTO_LIGHTBOX_MIN_ZOOM + 0.01

  return (
    <AppModal
      visible={visible}
      transparent
      animationType="fade"
      statusBarTranslucent
      onRequestClose={close}
    >
      <View
        testID={`${testIDPrefix}.overlay`}
        accessibilityViewIsModal
        style={{ flex: 1, backgroundColor: COLORS.black }}
      >
        <GestureDetector gesture={composed}>
          <Animated.View
            collapsable={false}
            testID={`${testIDPrefix}.canvas`}
            style={[{ flex: 1 }, imageStyle]}
          >
            {current && lightboxSource ? (
              <ExpoImage
                source={lightboxSource}
                placeholder={toPlaceholder(current.blurhash)}
                style={{ width: '100%', height: '100%', ...WEB_EXIF_ORIENTATION_STYLE }}
                contentFit="contain"
                cachePolicy={IMAGE_CACHE_POLICY}
                pointerEvents="none"
                accessibilityRole="image"
                accessibilityLabel={current.accessibilityLabel ?? t('vehicle_detail.view_image')}
                testID={`${testIDPrefix}.image`}
              />
            ) : null}
          </Animated.View>
        </GestureDetector>

        <View
          pointerEvents="box-none"
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            paddingTop: insets.top + SPACING.lg,
            paddingBottom: insets.bottom + SPACING.lg,
            paddingHorizontal: SPACING.lg,
          }}
        >
          <View
            pointerEvents="box-none"
            style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <LightboxControl
              label={t('photo_lightbox.close')}
              testID={`${testIDPrefix}.close`}
              onPress={close}
            >
              <X size={22} color={iconColor} strokeWidth={2.2} />
            </LightboxControl>
            <View style={{ flexDirection: 'row', gap: SPACING.sm }}>
              <LightboxControl
                label={t('photo_lightbox.zoom_out')}
                testID={`${testIDPrefix}.zoom-out`}
                disabled={!canZoomOut}
                onPress={() => {
                  selection()
                  applyZoom(stepPhotoZoom(zoom, -1))
                }}
              >
                <Minus size={20} color={iconColor} strokeWidth={2.2} />
              </LightboxControl>
              <LightboxControl
                label={t('photo_lightbox.zoom_in')}
                testID={`${testIDPrefix}.zoom-in`}
                disabled={!canZoomIn}
                onPress={() => {
                  selection()
                  applyZoom(stepPhotoZoom(zoom, 1))
                }}
              >
                <Plus size={20} color={iconColor} strokeWidth={2.2} />
              </LightboxControl>
            </View>
          </View>

          {hasMany ? (
            <View
              pointerEvents="box-none"
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingHorizontal: SPACING.lg,
              }}
            >
              <LightboxControl
                label={t('photo_lightbox.previous')}
                testID={`${testIDPrefix}.previous`}
                onPress={() => go(-1)}
              >
                <ChevronLeft size={22} color={iconColor} strokeWidth={2.2} />
              </LightboxControl>
              <LightboxControl
                label={t('photo_lightbox.next')}
                testID={`${testIDPrefix}.next`}
                onPress={() => go(1)}
              >
                <ChevronRight size={22} color={iconColor} strokeWidth={2.2} />
              </LightboxControl>
            </View>
          ) : null}

          {images.length > 0 ? (
            <View
              pointerEvents="none"
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: insets.bottom + SPACING.lg,
                alignItems: 'center',
              }}
            >
              <Text
                testID={`${testIDPrefix}.counter`}
                accessibilityLiveRegion="polite"
                style={{
                  fontFamily: FONTS.body,
                  fontSize: TEXT_STYLES.caption.fontSize,
                  lineHeight: TEXT_STYLES.caption.lineHeight,
                  color: COLORS.white,
                }}
              >
                {t('photo_lightbox.counter', {
                  current: safeIndex + 1,
                  total: images.length,
                })}
              </Text>
            </View>
          ) : null}
        </View>
      </View>
    </AppModal>
  )
}
